import { ButtonRowRight, Headline4 } from 'components/Elements';
import { ErrorMessage } from 'components/forms/FormComponents';
import useNeveForm from 'components/forms/NeveForm';
import TextField from 'components/forms/TextField';
import useUpsertRequest from 'hooks/useUpsertRequest';
import { APIError } from 'lib/_api-helpers';
import { logError } from 'lib/debug-helpers';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
    createErrorMessage,
    createGlobalErrorFromAPIError,
    fieldErrorCodes,
    requiredFieldErrorMessage,
} from 'shared/error-messages';
import styled from 'styled-components';
import { AuthContext } from 'contextProviders/AuthContext';
import { sizes } from 'theme';
import strings from '../../../strings/strings.json';
import Button from 'components/buttons/Button';

type OrganisationInput = {
    organisationName: string;
};

type UserCreateOrganisationFormProps = {
    showHeadlineText: boolean;
    maxWidth?: string;
    marketingTermsAccepted?: boolean;
};

export default function UserCreateOrganisationForm({
    showHeadlineText,
    maxWidth,
    marketingTermsAccepted,
}: UserCreateOrganisationFormProps): JSX.Element {
    const { refreshUserData } = React.useContext(AuthContext);
    const navigate = useNavigate();

    const { modifyData: upsertOrganisation } = useUpsertRequest('users/me/create-organisation');

    const [globalErrorMessage, setGlobalErrorMessage] = React.useState<string>('');

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useNeveForm<OrganisationInput>({
        organisationName: '',
    });

    async function onSubmit(formData: OrganisationInput) {
        const newOrganisation = {
            organisationName: formData.organisationName,
            ...(marketingTermsAccepted ? { marketingTermsAccepted: true } : {}),
        };
        const response = await upsertOrganisation(newOrganisation);

        const { errors: responseErrors } = response;
        if (responseErrors) {
            logError(responseErrors);
            updateErrors(responseErrors);
            return;
        }

        await refreshUserData();
        navigate('/organisation/dashboard/');
    }

    function updateErrors(apiErrors: APIError[]): void {
        apiErrors.forEach((apiError) => {
            const { field, code } = apiError;
            if (field === 'global') {
                return setGlobalErrorMessage(createGlobalErrorFromAPIError(code));
            }
        });
    }

    return (
        <CreateOrganisationForm onSubmit={handleSubmit(onSubmit)} $maxWidth={maxWidth}>
            {showHeadlineText && <Headline4>{strings.userCreateOrganisationPage.createOrgPageHeader}</Headline4>}
            <TextField
                fieldName="organisationName"
                labelText={strings.organisationForm.createOrganisationInputLabel}
                inputAria={strings.organisationForm.createOrganisationInputAriaLabel}
                errorMessage={errors.organisationName?.message}
                required
                maxLength={50}
                {...register('organisationName', {
                    required: {
                        value: true,
                        message: requiredFieldErrorMessage(strings.organisationForm.createOrganisationInputLabel),
                    },
                    maxLength: {
                        value: 50,
                        message: createErrorMessage(
                            `${strings.notifyAllUsersPage.messageBodyInputField.inputLabel}`,
                            fieldErrorCodes.maxLength,
                        ),
                    },
                })}
            />
            {globalErrorMessage && <ErrorMessage id="errorMessage">{globalErrorMessage}</ErrorMessage>}
            <ButtonRowRight>
                <Button
                    type="submit"
                    title={strings.userCreateOrganisationPage.button.title}
                    aria={strings.userCreateOrganisationPage.button.aria}
                />
            </ButtonRowRight>
        </CreateOrganisationForm>
    );
}

const CreateOrganisationForm = styled.form<{ $maxWidth?: string }>`
    display: flex;
    flex-direction: column;
    gap: ${sizes.spacingRg};
    width: 100%;
    max-width: ${({ $maxWidth }) => {
        return $maxWidth ? $maxWidth : '640px';
    }};
    margin: 0 auto;
`;
